import { supabase } from '../lib/supabaseClient';
import { supabaseUserService } from './supabaseUserService';
import supabaseEmailService from './supabaseEmailService';

export type RequestType = 'EXTRA_HOURS' | 'TIL_REQUEST';
export type RequestStatus = 'pending' | 'approved' | 'rejected' | 'cancelled' | 'cancelled_by_organiser';

export interface SupabaseRequest {
  id: string;
  user_id: string;
  user_name: string;
  employee_id: string;
  department_id: string | null;
  department_name: string;
  type: RequestType;
  status: RequestStatus;
  date: string;
  start_time: string | null;
  end_time: string | null;
  total_minutes: number;
  reason: string;
  is_multi_day: boolean;
  end_date: string | null;
  day_keys: string[] | null;
  approver_id: string | null;
  approver_name: string | null;
  approved_at: string | null;
  rejection_reason: string | null;
  priority: 'normal' | 'urgent';
  submitted_at: string;
  created_at: string;
  updated_at: string;
  // Cancellation fields (for cancelled_by_organiser status)
  cancelled_by_id?: string | null;
  cancelled_by_name?: string | null;
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

class SupabaseRequestService {
  private listeners: Array<(requests: SupabaseRequest[]) => void> = [];

  // Subscribe to request changes
  subscribe(listener: (requests: SupabaseRequest[]) => void) {
    this.listeners.push(listener);
    
    // Set up real-time subscription
    const channel = supabase
      .channel('requests_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'requests' }, () => {
        this.notifyListeners();
      })
      .subscribe();

    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
      supabase.removeChannel(channel);
    };
  }

  // Notify all listeners of changes
  private async notifyListeners() {
    const requests = await this.getAllRequests();
    this.listeners.forEach(listener => listener(requests));
  }

  // Get all requests
  async getAllRequests(): Promise<SupabaseRequest[]> {
    const { data, error } = await supabase
      .from('requests')
      .select('*')
      .order('submitted_at', { ascending: false });

    if (error) {
      console.error('Error fetching requests:', error);
      return [];
    }

    return data as SupabaseRequest[];
  }

  // Get requests by user
  async getRequestsByUser(userId: string): Promise<SupabaseRequest[]> {
    const { data, error } = await supabase
      .from('requests')
      .select('*')
      .eq('user_id', userId)
      .order('submitted_at', { ascending: false });

    if (error) {
      console.error('Error fetching user requests:', error);
      return [];
    }

    return data as SupabaseRequest[];
  }

  // Get requests by status
  async getRequestsByStatus(status: RequestStatus): Promise<SupabaseRequest[]> {
    const { data, error } = await supabase
      .from('requests')
      .select('*')
      .eq('status', status)
      .order('submitted_at', { ascending: false });

    if (error) {
      console.error('Error fetching requests by status:', error);
      return [];
    }

    return data as SupabaseRequest[];
  }

  // Get requests by type
  async getRequestsByType(type: RequestType): Promise<SupabaseRequest[]> {
    const { data, error } = await supabase
      .from('requests')
      .select('*')
      .eq('type', type)
      .order('submitted_at', { ascending: false });

    if (error) {
      console.error('Error fetching requests by type:', error);
      return [];
    }

    return data as SupabaseRequest[];
  }

  // Get TIL requests for organiser approval
  async getTILRequestsForOrganiser(): Promise<SupabaseRequest[]> {
    const { data, error } = await supabase
      .from('requests')
      .select('*')
      .eq('type', 'TIL_REQUEST')
      // Don't filter by status - return all TIL requests (pending, approved, rejected)
      // The UI will filter them into tabs
      .order('submitted_at', { ascending: false });

    if (error) {
      console.error('Error fetching TIL requests:', error);
      return [];
    }

    return data as SupabaseRequest[];
  }

  // Get Extra Hours requests for specific department
  async getExtraHoursRequestsForDepartment(departmentId: string): Promise<SupabaseRequest[]> {
    const { data, error } = await supabase
      .from('requests')
      .select('*')
      .eq('type', 'EXTRA_HOURS')
      .eq('status', 'pending')
      .eq('department_id', departmentId)
      .order('submitted_at', { ascending: false });

    if (error) {
      console.error('Error fetching extra hours requests:', error);
      return [];
    }

    return data as SupabaseRequest[];
  }

  // Submit Extra Hours Request
  async submitExtraHoursRequest(data: {
    userId: string;
    userName: string;
    employeeId: string;
    departmentId: string | null;
    departmentName: string;
    date: string;
    startTime: string;
    endTime: string;
    reason: string;
    totalMinutes: number;
  }): Promise<SupabaseRequest | null> {
    // IMPORTANT: Always fetch the user's current department from the database
    // to avoid stale data from the client-side auth context (fixes bug where
    // newly registered users had null department_id in their requests)
    let departmentId = data.departmentId;
    let departmentName = data.departmentName;

    try {
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('department_id')
        .eq('id', data.userId)
        .single();

      if (!userError && userData?.department_id) {
        departmentId = userData.department_id;

        // Also fetch the department name if we got a department_id
        const { data: deptData } = await supabase
          .from('departments')
          .select('name')
          .eq('id', userData.department_id)
          .single();

        if (deptData?.name) {
          departmentName = deptData.name;
        }

        console.log(`[REQUEST] Fetched department from DB: ${departmentName} (${departmentId})`);
      }
    } catch (lookupError) {
      console.warn('[REQUEST] Could not fetch user department, using provided values:', lookupError);
    }

    const { data: newRequest, error } = await supabase
      .from('requests')
      .insert([{
        user_id: data.userId,
        user_name: data.userName,
        employee_id: data.employeeId,
        department_id: departmentId,
        department_name: departmentName,
        type: 'EXTRA_HOURS',
        status: 'pending',
        date: data.date,
        start_time: data.startTime,
        end_time: data.endTime,
        total_minutes: data.totalMinutes,
        reason: data.reason,
        priority: 'normal',
      }])
      .select()
      .single();

    if (error) {
      console.error('Error submitting extra hours request:', error);
      return null;
    }

    // Log the request
    await supabase.from('system_logs').insert({
      user_id: data.userId,
      user_name: data.userName,
      action: 'submit_extra_hours_request',
      target_type: 'request',
      target_id: newRequest.id,
      details: { type: 'EXTRA_HOURS', minutes: data.totalMinutes },
    });

    // Send email notification to department leader
    let leaderName = 'Department Leader'; // Default fallback
    if (data.departmentId) {
      try {
        console.log(`[EMAIL DEBUG] Looking up leader for department_id: ${data.departmentId}, department_name: ${data.departmentName}`);

        const { data: department } = await supabase
          .from('departments')
          .select('leader_id')
          .eq('id', data.departmentId)
          .single();

        console.log(`[EMAIL DEBUG] Found department with leader_id: ${department?.leader_id}`);

        if (department?.leader_id) {
          const { data: leader } = await supabase
            .from('users')
            .select('id, full_name, email')
            .eq('id', department.leader_id)
            .single();

          console.log(`[EMAIL DEBUG] Leader found: ${leader?.full_name} (${leader?.email})`);

          if (leader && leader.email) {
            leaderName = leader.full_name; // Store for receipt email
            console.log(`[EMAIL DEBUG] Sending Extra Hours notification email to: ${leader.email} for request from ${data.userName}`);

            await supabaseEmailService.notifyLeaderExtraHoursSubmitted({
              leaderId: leader.id,
              leaderName: leader.full_name,
              leaderEmail: leader.email,
              staffName: data.userName,
              staffEmployeeId: data.employeeId,
              departmentName: data.departmentName,
              requestId: newRequest.id,
              requestDate: new Date().toLocaleDateString(),
              workDate: new Date(data.date).toLocaleDateString(),
              startTime: data.startTime,
              endTime: data.endTime,
              totalHours: Math.round(data.totalMinutes / 60 * 100) / 100,
              reason: data.reason,
            });

            console.log(`[EMAIL DEBUG] Email sent successfully`);
          } else {
            console.warn(`[EMAIL DEBUG] Leader has no email address - cannot send notification`);
          }
        } else {
          console.warn(`[EMAIL DEBUG] Department has no leader assigned - cannot send notification`);
        }
      } catch (emailError) {
        console.error('Error sending email notification to leader:', emailError);
        // Don't fail the request if email fails
      }
    }

    // Send receipt email to requester
    try {
      const { data: requester } = await supabase
        .from('users')
        .select('email')
        .eq('id', data.userId)
        .single();

      if (requester && requester.email) {
        console.log(`[EMAIL DEBUG] Sending Extra Hours receipt email to: ${requester.email}`);

        await supabaseEmailService.notifyRequesterExtraHoursReceived({
          requesterId: data.userId,
          requesterEmail: requester.email,
          staffName: data.userName,
          workDate: new Date(data.date).toLocaleDateString(),
          startTime: data.startTime,
          endTime: data.endTime,
          totalHours: Math.round(data.totalMinutes / 60 * 100) / 100,
          reason: data.reason,
          departmentName: data.departmentName,
          leaderName,
          requestId: newRequest.id,
          submittedDate: new Date().toLocaleDateString(),
        });

        console.log(`[EMAIL DEBUG] Receipt email sent successfully`);
      }
    } catch (emailError) {
      console.error('Error sending receipt email to requester:', emailError);
      // Don't fail the request if email fails
    }

    await this.notifyListeners();
    return newRequest as SupabaseRequest;
  }

  // Submit TIL Request
  async submitTILRequest(data: {
    userId: string;
    userName: string;
    employeeId: string;
    departmentId: string | null;
    departmentName: string;
    date: string;
    startTime: string;
    endTime: string;
    reason: string;
    totalMinutes: number;
    isMultiDay?: boolean;
    endDate?: string;
    dayKeys?: string[];
  }): Promise<SupabaseRequest | null> {
    // IMPORTANT: Always fetch the user's current department from the database
    // to avoid stale data from the client-side auth context (fixes bug where
    // newly registered users had null department_id in their requests)
    let departmentId = data.departmentId;
    let departmentName = data.departmentName;

    try {
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('department_id')
        .eq('id', data.userId)
        .single();

      if (!userError && userData?.department_id) {
        departmentId = userData.department_id;

        // Also fetch the department name if we got a department_id
        const { data: deptData } = await supabase
          .from('departments')
          .select('name')
          .eq('id', userData.department_id)
          .single();

        if (deptData?.name) {
          departmentName = deptData.name;
        }

        console.log(`[REQUEST] Fetched department from DB: ${departmentName} (${departmentId})`);
      }
    } catch (lookupError) {
      console.warn('[REQUEST] Could not fetch user department, using provided values:', lookupError);
    }

    const { data: newRequest, error } = await supabase
      .from('requests')
      .insert([{
        user_id: data.userId,
        user_name: data.userName,
        employee_id: data.employeeId,
        department_id: departmentId,
        department_name: departmentName,
        type: 'TIL_REQUEST',
        status: 'pending',
        date: data.date,
        start_time: data.startTime,
        end_time: data.endTime,
        total_minutes: data.totalMinutes,
        reason: data.reason,
        is_multi_day: data.isMultiDay || false,
        end_date: data.endDate || null,
        day_keys: data.dayKeys || null,
        priority: 'normal',
      }])
      .select()
      .single();

    if (error) {
      console.error('Error submitting TIL request:', error);
      return null;
    }

    // Log the request
    await supabase.from('system_logs').insert({
      user_id: data.userId,
      user_name: data.userName,
      action: 'submit_til_request',
      target_type: 'request',
      target_id: newRequest.id,
      details: { type: 'TIL_REQUEST', minutes: data.totalMinutes },
    });

    // Send email notification to all organisers
    try {
      const { data: organisers } = await supabase
        .from('users')
        .select('id, full_name, email')
        .contains('roles', ['Organiser']);

      if (organisers && organisers.length > 0) {
        // Get user's current balance
        const { data: user } = await supabase
          .from('users')
          .select('til_balance')
          .eq('id', data.userId)
          .single();

        const currentBalanceHours = user ? Math.round(user.til_balance / 60 * 100) / 100 : 0;
        const requestHours = Math.round(data.totalMinutes / 60 * 100) / 100;
        const balanceAfter = Math.round((currentBalanceHours - requestHours) * 100) / 100;

        // Format dates for email
        let tilDates = new Date(data.date).toLocaleDateString();
        if (data.isMultiDay && data.endDate) {
          tilDates += ` - ${new Date(data.endDate).toLocaleDateString()}`;
        }

        await supabaseEmailService.notifyOrganisersTILRequestSubmitted({
          organisers: organisers.filter(o => o.email),
          staffName: data.userName,
          staffEmployeeId: data.employeeId,
          departmentName: data.departmentName,
          requestId: newRequest.id,
          requestDate: new Date().toLocaleDateString(),
          tilDates,
          timeRange: `${data.startTime} - ${data.endTime}`,
          totalHours: requestHours,
          currentBalance: currentBalanceHours,
          balanceAfterApproval: balanceAfter,
          reason: data.reason,
        });
      }
    } catch (emailError) {
      console.error('Error sending email notification to organisers:', emailError);
      // Don't fail the request if email fails
    }

    // Send receipt email to requester
    try {
      const { data: requester } = await supabase
        .from('users')
        .select('email, til_balance')
        .eq('id', data.userId)
        .single();

      if (requester && requester.email) {
        console.log(`[EMAIL DEBUG] Sending TIL Request receipt email to: ${requester.email}`);

        const currentBalanceHours = Math.round(requester.til_balance / 60 * 100) / 100;
        const requestHours = Math.round(data.totalMinutes / 60 * 100) / 100;
        const balanceAfter = Math.round((currentBalanceHours - requestHours) * 100) / 100;

        // Format dates for email
        let tilDates = new Date(data.date).toLocaleDateString();
        if (data.isMultiDay && data.endDate) {
          tilDates += ` - ${new Date(data.endDate).toLocaleDateString()}`;
        }

        await supabaseEmailService.notifyRequesterTILRequestReceived({
          requesterId: data.userId,
          requesterEmail: requester.email,
          staffName: data.userName,
          tilDates,
          timeRange: `${data.startTime} - ${data.endTime}`,
          totalHours: requestHours,
          reason: data.reason,
          currentBalance: currentBalanceHours,
          balanceAfterApproval: balanceAfter,
          requestId: newRequest.id,
          submittedDate: new Date().toLocaleDateString(),
        });

        console.log(`[EMAIL DEBUG] Receipt email sent successfully`);
      }
    } catch (emailError) {
      console.error('Error sending receipt email to requester:', emailError);
      // Don't fail the request if email fails
    }

    await this.notifyListeners();
    return newRequest as SupabaseRequest;
  }

  // Approve request
  async approveRequest(requestId: string, approverId: string, approverName: string, comments?: string): Promise<boolean> {
    // Get the request first
    const { data: request, error: fetchError } = await supabase
      .from('requests')
      .select('*')
      .eq('id', requestId)
      .single();

    if (fetchError || !request) {
      console.error('Error fetching request:', fetchError);
      return false;
    }

    // Update request status
    const { error: updateError } = await supabase
      .from('requests')
      .update({
        status: 'approved',
        approver_id: approverId,
        approver_name: approverName,
        approved_at: new Date().toISOString(),
        rejection_reason: comments || null,
      })
      .eq('id', requestId);

    if (updateError) {
      console.error('Error approving request:', updateError);
      return false;
    }

    // Update TIL balance based on request type
    if (request.type === 'EXTRA_HOURS') {
      await supabaseUserService.addTILBalance(request.user_id, request.total_minutes, requestId);
    } else if (request.type === 'TIL_REQUEST') {
      await supabaseUserService.deductTILBalance(request.user_id, request.total_minutes, requestId);
    }

    // Log the approval
    await supabase.from('system_logs').insert({
      user_id: approverId,
      user_name: approverName,
      action: 'approve_request',
      target_type: 'request',
      target_id: requestId,
      details: { type: request.type, status: 'approved' },
    });

    // Create notification for user
    const { data: notification } = await supabase.from('notifications').insert({
      to_user_id: request.user_id,
      from_user_id: approverId,
      title: 'Request Approved',
      message: `Your ${request.type === 'EXTRA_HOURS' ? 'extra hours' : 'TIL'} request has been approved.`,
      type: 'request_approved',
      priority: 'normal',
    }).select().single();

    // Send email notification to requester
    try {
      console.log(`[APPROVAL EMAIL DEBUG] Starting approval email process for request ${requestId}`);
      console.log(`[APPROVAL EMAIL DEBUG] Request type: ${request.type}, User ID: ${request.user_id}`);

      const { data: requester, error: requesterError } = await supabase
        .from('users')
        .select('id, full_name, email, til_balance, department_id, employee_id')
        .eq('id', request.user_id)
        .single();

      if (requesterError) {
        console.error('[APPROVAL EMAIL DEBUG] Error fetching requester:', requesterError);
        throw new Error(`Failed to fetch requester: ${requesterError.message}`);
      }

      console.log(`[APPROVAL EMAIL DEBUG] Requester found: ${requester?.full_name}, Email: ${requester?.email}`);

      if (!requester) {
        console.warn('[APPROVAL EMAIL DEBUG] No requester found for user_id:', request.user_id);
        throw new Error('Requester not found');
      }

      if (!requester.email) {
        console.warn('[APPROVAL EMAIL DEBUG] Requester has no email address');
        throw new Error('Requester has no email');
      }

      const totalHours = Math.round(request.total_minutes / 60 * 100) / 100;
      const newBalanceHours = Math.round(requester.til_balance / 60 * 100) / 100;

      console.log(`[APPROVAL EMAIL DEBUG] Sending ${request.type} approval email to ${requester.email}`);

      if (request.type === 'EXTRA_HOURS') {
        // Format dates for email
        let tilDates = new Date(request.date).toLocaleDateString();
        if (request.is_multi_day && request.end_date) {
          tilDates += ` - ${new Date(request.end_date).toLocaleDateString()}`;
        }

        const result = await supabaseEmailService.notifyRequesterExtraHoursApproved({
          requesterId: requester.id,
          requesterEmail: requester.email,
          staffName: requester.full_name,
          approverName,
          requestId,
          workDate: new Date(request.date).toLocaleDateString(),
          startTime: request.start_time || 'N/A',
          endTime: request.end_time || 'N/A',
          totalHours,
          approvedDate: new Date().toLocaleDateString(),
          newTILBalance: newBalanceHours,
          comments,
          notificationId: notification?.id,
        });

        console.log(`[APPROVAL EMAIL DEBUG] Extra Hours approval email result:`, result);
      } else if (request.type === 'TIL_REQUEST') {
        let tilDates = new Date(request.date).toLocaleDateString();
        if (request.is_multi_day && request.end_date) {
          tilDates += ` - ${new Date(request.end_date).toLocaleDateString()}`;
        }

        const result = await supabaseEmailService.notifyRequesterTILRequestApproved({
          requesterId: requester.id,
          requesterEmail: requester.email,
          staffName: requester.full_name,
          approverName,
          requestId,
          tilDates,
          timeRange: `${request.start_time} - ${request.end_time}`,
          totalHours,
          approvedDate: new Date().toLocaleDateString(),
          newTILBalance: newBalanceHours,
          comments,
          notificationId: notification?.id,
        });

        console.log(`[APPROVAL EMAIL DEBUG] TIL approval email result:`, result);

        // Also notify the department leader about the TIL approval
        if (requester.department_id) {
          console.log(`[LEADER EMAIL DEBUG] Looking up leader for department: ${requester.department_id}`);

          const { data: leader, error: leaderError } = await supabase
            .from('users')
            .select('id, full_name, email')
            .eq('leader_of_department', requester.department_id)
            .eq('status', 'active')
            .single();

          if (leaderError) {
            console.warn(`[LEADER EMAIL DEBUG] No leader found for department ${requester.department_id}:`, leaderError);
          } else if (leader && leader.email) {
            console.log(`[LEADER EMAIL DEBUG] Found leader: ${leader.full_name} (${leader.email})`);

            const leaderResult = await supabaseEmailService.notifyLeaderTILApproved({
              leaderId: leader.id,
              leaderName: leader.full_name,
              leaderEmail: leader.email,
              staffName: requester.full_name,
              staffEmployeeId: requester.employee_id || 'N/A',
              departmentName: request.department_name || 'Unknown Department',
              requestId,
              tilDates,
              timeRange: `${request.start_time} - ${request.end_time}`,
              totalHours,
              approvedDate: new Date().toLocaleDateString(),
              approverName,
              reason: request.reason,
            });

            console.log(`[LEADER EMAIL DEBUG] Leader notification email result:`, leaderResult);
          } else {
            console.warn(`[LEADER EMAIL DEBUG] Leader found but has no email address`);
          }
        } else {
          console.log(`[LEADER EMAIL DEBUG] Requester has no department_id, skipping leader notification`);
        }
      }
    } catch (emailError) {
      console.error('[APPROVAL EMAIL DEBUG] Error sending approval email:', emailError);
      // Don't fail the approval if email fails
    }

    await this.notifyListeners();
    return true;
  }

  // Reject request
  async rejectRequest(requestId: string, approverId: string, approverName: string, reason?: string): Promise<boolean> {
    // Get the request first
    const { data: request, error: fetchError } = await supabase
      .from('requests')
      .select('*')
      .eq('id', requestId)
      .single();

    if (fetchError || !request) {
      console.error('Error fetching request:', fetchError);
      return false;
    }

    // Update request status
    const { error: updateError } = await supabase
      .from('requests')
      .update({
        status: 'rejected',
        approver_id: approverId,
        approver_name: approverName,
        approved_at: new Date().toISOString(),
        rejection_reason: reason || 'Request rejected',
      })
      .eq('id', requestId);

    if (updateError) {
      console.error('Error rejecting request:', updateError);
      return false;
    }

    // Log the rejection
    await supabase.from('system_logs').insert({
      user_id: approverId,
      user_name: approverName,
      action: 'reject_request',
      target_type: 'request',
      target_id: requestId,
      details: { type: request.type, status: 'rejected', reason },
    });

    // Create notification for user
    const { data: notification } = await supabase.from('notifications').insert({
      to_user_id: request.user_id,
      from_user_id: approverId,
      title: 'Request Rejected',
      message: `Your ${request.type === 'EXTRA_HOURS' ? 'extra hours' : 'TIL'} request has been rejected. Reason: ${reason || 'No reason provided'}`,
      type: 'request_rejected',
      priority: 'normal',
    }).select().single();

    // Send email notification to requester
    try {
      const { data: requester } = await supabase
        .from('users')
        .select('id, full_name, email, til_balance')
        .eq('id', request.user_id)
        .single();

      if (requester && requester.email) {
        const totalHours = Math.round(request.total_minutes / 60 * 100) / 100;

        if (request.type === 'EXTRA_HOURS') {
          await supabaseEmailService.notifyRequesterExtraHoursRejected({
            requesterId: requester.id,
            requesterEmail: requester.email,
            staffName: requester.full_name,
            approverName,
            requestId,
            workDate: new Date(request.date).toLocaleDateString(),
            startTime: request.start_time || 'N/A',
            endTime: request.end_time || 'N/A',
            totalHours,
            rejectedDate: new Date().toLocaleDateString(),
            rejectionReason: reason || 'No reason provided',
            notificationId: notification?.id,
          });
        } else if (request.type === 'TIL_REQUEST') {
          let tilDates = new Date(request.date).toLocaleDateString();
          if (request.is_multi_day && request.end_date) {
            tilDates += ` - ${new Date(request.end_date).toLocaleDateString()}`;
          }

          const currentBalanceHours = Math.round(requester.til_balance / 60 * 100) / 100;

          await supabaseEmailService.notifyRequesterTILRequestRejected({
            requesterId: requester.id,
            requesterEmail: requester.email,
            staffName: requester.full_name,
            approverName,
            requestId,
            tilDates,
            timeRange: `${request.start_time} - ${request.end_time}`,
            totalHours,
            rejectedDate: new Date().toLocaleDateString(),
            rejectionReason: reason || 'No reason provided',
            currentBalance: currentBalanceHours,
            notificationId: notification?.id,
          });
        }
      }
    } catch (emailError) {
      console.error('Error sending rejection email:', emailError);
      // Don't fail the rejection if email fails
    }

    await this.notifyListeners();
    return true;
  }

  // Cancel request (by requester)
  async cancelRequest(requestId: string, userId: string, userName: string): Promise<boolean> {
    const { error } = await supabase
      .from('requests')
      .update({
        status: 'cancelled',
        rejection_reason: 'Cancelled by user',
      })
      .eq('id', requestId)
      .eq('user_id', userId)
      .eq('status', 'pending');

    if (error) {
      console.error('Error cancelling request:', error);
      return false;
    }

    // Log the cancellation
    await supabase.from('system_logs').insert({
      user_id: userId,
      user_name: userName,
      action: 'cancel_request',
      target_type: 'request',
      target_id: requestId,
    });

    await this.notifyListeners();
    return true;
  }

  // Get request by ID
  async getRequestById(requestId: string): Promise<SupabaseRequest | null> {
    const { data, error } = await supabase
      .from('requests')
      .select('*')
      .eq('id', requestId)
      .single();

    if (error) {
      console.error('Error fetching request:', error);
      return null;
    }

    return data as SupabaseRequest;
  }

  // Get approved TIL requests for calendar view
  async getApprovedTILRequestsForCalendar(): Promise<SupabaseRequest[]> {
    const { data, error } = await supabase
      .from('requests')
      .select('*')
      .eq('type', 'TIL_REQUEST')
      .eq('status', 'approved')
      .order('date', { ascending: true });

    if (error) {
      console.error('Error fetching approved TIL requests:', error);
      return [];
    }

    return data as SupabaseRequest[];
  }

  // Get request statistics
  async getRequestStatistics() {
    const { data: allRequests, error } = await supabase
      .from('requests')
      .select('*');

    if (error) {
      console.error('Error fetching request statistics:', error);
      return {
        total: 0,
        pending: 0,
        approved: 0,
        rejected: 0,
        cancelled: 0,
        tilRequests: 0,
        extraHoursRequests: 0,
        recentRequests: 0,
        approvalRate: 0,
      };
    }

    const total = allRequests.length;
    const pending = allRequests.filter(r => r.status === 'pending').length;
    const approved = allRequests.filter(r => r.status === 'approved').length;
    const rejected = allRequests.filter(r => r.status === 'rejected').length;
    const cancelled = allRequests.filter(r => r.status === 'cancelled').length;
    const tilRequests = allRequests.filter(r => r.type === 'TIL_REQUEST').length;
    const extraHoursRequests = allRequests.filter(r => r.type === 'EXTRA_HOURS').length;

    // Calculate recent activity (last 7 days)
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const recentRequests = allRequests.filter(r =>
      new Date(r.submitted_at) >= sevenDaysAgo
    ).length;

    return {
      total,
      pending,
      approved,
      rejected,
      cancelled,
      tilRequests,
      extraHoursRequests,
      recentRequests,
      approvalRate: total > 0 ? Math.round((approved / total) * 100) : 0,
    };
  }

  // Get all approved requests for organiser management
  async getApprovedRequestsForOrganiser(): Promise<SupabaseRequest[]> {
    const { data, error } = await supabase
      .from('requests')
      .select('*')
      .eq('status', 'approved')
      .order('approved_at', { ascending: false });

    if (error) {
      console.error('Error fetching approved requests:', error);
      return [];
    }

    return data as SupabaseRequest[];
  }

  // Cancel an approved request (organiser only)
  async cancelApprovedRequest(
    requestId: string,
    organiserId: string,
    organiserName: string,
    reason?: string
  ): Promise<{ success: boolean; error?: string; newBalance?: number }> {
    // Get the request first
    const { data: request, error: fetchError } = await supabase
      .from('requests')
      .select('*')
      .eq('id', requestId)
      .single();

    if (fetchError || !request) {
      console.error('Error fetching request:', fetchError);
      return { success: false, error: 'Request not found' };
    }

    // Verify the request is approved
    if (request.status !== 'approved') {
      return { success: false, error: 'Only approved requests can be cancelled' };
    }

    // Calculate the balance adjustment
    // EXTRA_HOURS: was added, so subtract (negative delta)
    // TIL_REQUEST: was subtracted, so add back (positive delta)
    const balanceDelta = request.type === 'EXTRA_HOURS'
      ? -request.total_minutes
      : request.total_minutes;

    // Get the user's current balance before adjustment
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('til_balance, email, full_name')
      .eq('id', request.user_id)
      .single();

    if (userError || !userData) {
      console.error('Error fetching user:', userError);
      return { success: false, error: 'User not found' };
    }

    const previousBalance = userData.til_balance || 0;

    // Update request status to cancelled_by_organiser
    const { error: updateError } = await supabase
      .from('requests')
      .update({
        status: 'cancelled_by_organiser',
        cancelled_by_id: organiserId,
        cancelled_by_name: organiserName,
        cancelled_at: new Date().toISOString(),
        cancellation_reason: reason || null,
      })
      .eq('id', requestId);

    if (updateError) {
      console.error('Error updating request status:', updateError);
      return { success: false, error: 'Failed to update request status' };
    }

    // Adjust the user's TIL balance
    const description = reason
      ? `Request cancelled by organiser: ${reason}`
      : 'Request cancelled by organiser';

    const adjustResult = await supabaseUserService.adjustTILBalance(
      request.user_id,
      balanceDelta,
      requestId,
      description
    );

    if (!adjustResult.success) {
      // Rollback the status change
      await supabase
        .from('requests')
        .update({
          status: 'approved',
          cancelled_by_id: null,
          cancelled_by_name: null,
          cancelled_at: null,
          cancellation_reason: null,
        })
        .eq('id', requestId);

      return { success: false, error: adjustResult.error || 'Failed to adjust balance' };
    }

    const newBalance = adjustResult.newBalance || (previousBalance + balanceDelta);

    // Log the cancellation
    await supabase.from('system_logs').insert({
      user_id: organiserId,
      user_name: organiserName,
      action: 'cancel_approved_request',
      target_type: 'request',
      target_id: requestId,
      details: {
        affected_user: request.user_name,
        affected_user_id: request.user_id,
        request_type: request.type,
        minutes_reversed: Math.abs(balanceDelta),
        reason: reason || null,
        previous_balance: previousBalance,
        new_balance: newBalance,
      },
    });

    // Create notification for user
    const { data: notification } = await supabase.from('notifications').insert({
      to_user_id: request.user_id,
      from_user_id: organiserId,
      title: 'Request Cancelled',
      message: `Your ${request.type === 'EXTRA_HOURS' ? 'extra hours' : 'TIL'} request for ${new Date(request.date).toLocaleDateString()} has been cancelled by ${organiserName}. ${reason ? `Reason: ${reason}` : ''}`,
      type: 'request_cancelled',
      priority: 'normal',
    }).select().single();

    // Send email notification
    try {
      if (userData.email) {
        const totalHours = Math.round(request.total_minutes / 60 * 100) / 100;
        const previousBalanceHours = Math.round(previousBalance / 60 * 100) / 100;
        const balanceAdjustmentHours = Math.round(balanceDelta / 60 * 100) / 100;
        const newBalanceHours = Math.round(newBalance / 60 * 100) / 100;

        await supabaseEmailService.notifyRequesterRequestCancelled({
          requesterId: request.user_id,
          requesterEmail: userData.email,
          staffName: userData.full_name,
          organiserName,
          requestId,
          requestType: request.type,
          requestDate: new Date(request.date).toLocaleDateString(),
          totalHours,
          previousBalance: previousBalanceHours,
          balanceAdjustment: balanceAdjustmentHours,
          newBalance: newBalanceHours,
          cancellationReason: reason,
          notificationId: notification?.id,
        });
      }
    } catch (emailError) {
      console.error('Error sending cancellation email:', emailError);
      // Don't fail the cancellation if email fails
    }

    await this.notifyListeners();
    return { success: true, newBalance };
  }
}

// Create and export singleton instance
export const supabaseRequestService = new SupabaseRequestService();
